import {MouseEvent, useEffect, useState} from "react";
import {RouteComponentProps, withRouter} from "react-router";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";
import {Breadcrumbs} from "@rw/breadcrumbs";
import {appLink} from "@rw/cars_routes";
import {priceFormat} from "@rw/string_utils";
import {carsTheme} from "@rw/themes";
import {isEmpty} from "lodash";

import {setFavouriteCar} from "../../../../app/actions/load_local_storage_favourites_to_store";
import {PageContainer} from "../../../../app/atoms/PageContainer";
import {MainPage} from "../../../../app/components/MainPage";
import {SliderComponent} from "../../../../app/components/Slider";
import {CarMakeTypes} from "../../../../cars_data/cars_data";
import {engineTypes, getLabelByValue} from "../../../../dashboard/add_car/options/select_options";
import {bgc_primary, bgc_primary_hover} from "../../../../styles/helpers";
import {getThemeBreakpointCorrect} from "../../../../styles/linaria_variable_factory";
import {hitOfferViewEvent} from "../../../../tracking/common/hit_offer_view_event";
import {getViewTypeName, ViewType} from "../../../../tracking/view_type/view_type";
import {IBreadcrumb} from "../../../list/components/CarListDynamic";
import {getOfferListUrl} from "../../../list/url_utils/get_offer_list_url";
import {SchemaCarOffer} from "../../schemas/SchemaCarOffer";
import {CarDetailsOptionList} from "../CarDetailsOptionList";
import {OfferRelatedLinks} from "../OfferRelatedLinks";
import {UserInfo} from "../UserInfo";

function getCarDetailsPL(acf: {[key: string]: unknown}): {[key: string]: string} {
    // allowedFields przechowuje nazwę po polsku oraz opcjonalny sufiks
    const allowedFields: {
        [key: string]: {label: string; suffix?: string};
    } = {
        year: {label: "Rok produkcji"},
        millage: {label: "Przebieg", suffix: " km"},
        engine: {label: "Rodzaj paliwa"},
        gearbox: {label: "Skrzynia biegów"},
        body_type: {label: "Typ nadwozia"},
        engine_size: {label: "Pojemność skokowa", suffix: " cm³"},
        horse_power: {label: "Moc", suffix: " KM"}
    };

    const result: {[key: string]: string} = {};

    for (const key in acf) {
        if (Object.prototype.hasOwnProperty.call(allowedFields, key)) {
            const field = allowedFields[key];
            const value = String(acf[key] ?? "");
            // Dodajemy wartość wraz z sufiksem, jeśli jest zdefiniowany
            result[field.label] = field.suffix ? `${value}${field.suffix}` : value;
        }
    }

    return result;
}

function getCarDetailsBasicPL(acf: {[key: string]: string}): {[key: string]: string} {
    const allowedFields: {[key: string]: string} = {
        version: "Wersja",
        generation: "Generacja",
        doors_counts: "Liczba drzwi",
        spots_counts: "Liczba miejsc",
        car_color: "Color",
        vin: "VIN"
    };

    const result: {[key: string]: string} = {};

    for (const key in acf) {
        if (Object.prototype.hasOwnProperty.call(allowedFields, key)) {
            result[allowedFields[key]] = acf[key];
        }
    }

    return result;
}

interface IProps extends RouteComponentProps<{}> {
    car: {
        id: number;
        title: string;
        content: string;

        slug: string;
        acf: {
            make: string;
            model: string;
            year: string;
            millage: string;
            engine: string;
            price: string;
            gallery?: any;
            gearbox: string;
            engine_size: string;
            horse_power: string;
            city: string;
            wojew: string;
            first_registation_date: string;
            sellers_name: string;
            phone_number: string;
            body_type: string;
            damaged: string; //'tak/nie'
            imported: string;
            filekeys?: string;
            gallery_order?: string[] | string;
        };
        details_basic: {
            version: string;
            generation: string;
            doors_counts: string;
            spots_counts: string;
            car_color: string;
            vin: string;
        };
        data_back?: any;
    };
    viewType?: ViewType | null;
    setFavouriteCar: typeof setFavouriteCar;
    favouriteCars: number[];
}

export const generateCarDetailsBreadcrumbs = (
    carMakeType: CarMakeTypes,
    model: string,
    city?: string,
    voivodeship?: string
) => {
    const listingBreadcrumbs: IBreadcrumb[] = [
        {
            label: "Osobowe",
            link: appLink.searchResult.universal({offerType: "osobowe"}),
            asStatic: false
        }
    ];

    const toSlug = (value: string) =>
        value
            .trim()
            .toLowerCase()
            .replace(/ą/g, "a")
            .replace(/ć/g, "c")
            .replace(/ę/g, "e")
            .replace(/ł/g, "l")
            .replace(/ń/g, "n")
            .replace(/ó/g, "o")
            .replace(/ś/g, "s")
            .replace(/ź|ż/g, "z")
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");

    if (carMakeType) {
        const makeSlug = carMakeType.toLowerCase();
        listingBreadcrumbs.push({
            label: `${carMakeType}`,
            link: getOfferListUrl({
                offerType: "osobowe",
                type: makeSlug
            })
        });

        if (voivodeship && city) {
            listingBreadcrumbs.push({
                label: city,
                link: getOfferListUrl({
                    offerType: "osobowe",
                    type: makeSlug,
                    location: toSlug(voivodeship),
                    subLocation: toSlug(city)
                })
            });
        } else if (model) {
            listingBreadcrumbs.push({
                label: `${model}`,
                link: getOfferListUrl({
                    offerType: "osobowe",
                    type: makeSlug,
                    model
                })
            });
        }

        return listingBreadcrumbs;
    }

    return listingBreadcrumbs;
};

const CarsDetailForSaleC = (props: IProps) => {
    const {engine_size, price, engine, year, sellers_name} = props.car.acf;
    const detailsRows = getCarDetailsPL(props.car.acf);
    const detailsBasic = getCarDetailsBasicPL(props.car.details_basic);
    const [isFavourite, setIsFavourite] = useState<boolean>(false);

    useEffect(() => {
        const isFavouriteValue = props.favouriteCars.includes(props.car.id);
        setIsFavourite(isFavouriteValue);
    }, [props.favouriteCars]);

    useEffect(() => {
        if (!props.car?.id) {
            return;
        }

        const {acf, id, slug} = props.car;
        hitOfferViewEvent({
            offer_id: String(id),
            offer_name: props.car.title,
            offer_slug: slug,
            make: acf.make,
            model: acf.model,
            year: acf.year,
            price: acf.price,
            city: acf.city,
            page_type: props.viewType ? getViewTypeName(props.viewType) : getViewTypeName(ViewType.OFFER)
        });
    }, [props.car.id]);

    const toggleFavourite = (e: MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        props.setFavouriteCar(props.car.id);
    };
    const formattedContent = props.car.content.replace(/\r\n|\n|\r/g, "<br />");

    //TODO remove casting
    const breadcrumbsData = generateCarDetailsBreadcrumbs(
        props.car.acf.make as CarMakeTypes,
        props.car.acf.model,
        props.car.acf.city,
        props.car.acf.wojew
    );

    return (
        <MainPage>
            <SchemaCarOffer car={props.car} />
            <PageContainer>
                <div css={{margin: "1rem 0"}}>
                    <CarDetailBreadcrumbs breadcrumbs={breadcrumbsData} />
                </div>
                <DetailWrapper isActive>
                    <Aside>
                        <TopAsideInfoWrapper>
                            <div>
                                <H1>{props.car.title}</H1>
                                <small>{`Rok produkcji: ${year}`}</small>
                            </div>
                            <Price>
                                {priceFormat(parseInt(price), {unit: ""})} <small css={small}>PLN</small>
                            </Price>
                            <UserInfo name={sellers_name} id={props.car.id} />
                        </TopAsideInfoWrapper>
                        <div></div>
                        {!isFavourite ? (
                            <button onClick={toggleFavourite} css={fav}>
                                Dodaj do ulubionych
                            </button>
                        ) : (
                            <button onClick={toggleFavourite} css={fav}>
                                Usuń z ulubionych
                            </button>
                        )}
                    </Aside>
                    <div css={sliderHolder}>
                        <SliderComponent src={props.car.acf.gallery} aws={props.car.acf.filekeys} order={props.car.acf.gallery_order} />
                    </div>
                </DetailWrapper>
            </PageContainer>

            <PageContainer>
                <Section>
                    <h2>Opis</h2>
                    <div dangerouslySetInnerHTML={{__html: formattedContent}} />
                </Section>
                <Section css={carInfo}>
                    <div css={carDetailsWrapper}>
                        <h2>Szczegóły</h2>

                        {Object.entries(detailsRows).map(([key, value]) => {
                            const label = key == "Rodzaj paliwa" ? getLabelByValue(value, engineTypes) : value;
                            return (
                                <CarDetails key={key}>
                                    <div css={detailRow}>
                                        <p css={[pRow, pKey]}>{key}</p>
                                        <p css={[pRow, pValue]}>{label}</p>
                                    </div>
                                </CarDetails>
                            );
                        })}
                    </div>
                    <div css={carDetailsWrapper}>
                        <h2>Podstawowe informacje</h2>

                        {Object.entries(detailsBasic)
                            .filter(([_, value]) => !isEmpty(value))
                            .map(([key, value]) => (
                                <CarDetails key={key}>
                                    <div css={detailRow}>
                                        <p css={pRow}>{key}</p>
                                        <p css={[pRow, pValue]}>{value}</p>
                                    </div>
                                </CarDetails>
                            ))}
                    </div>
                </Section>
                <Section>
                    <CarDetailsOptionList />
                </Section>
                <OfferRelatedLinks
                    make={props.car.acf.make}
                    model={props.car.acf.model}
                    city={props.car.acf.city}
                    voivodeship={props.car.acf.wojew}
                />
            </PageContainer>
        </MainPage>
    );
};

const sliderHolder = (theme: Theme) => css`
    width: 100%;
    margin-top: 3rem;

    @media (min-width: ${theme.breakpoints.screen_md}) {
        width: calc(100% - 30rem);
        margin-top: 0;
    }
`;

const carInfo = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    gap: 4rem;
    justify-content: space-between;

    @media (min-width: ${theme.breakpoints.screen_xs}) {
        flex-direction: row;
    }
`;

const TopAsideInfoWrapper = styled.div`
    display: flex;
    flex-direction: column;

    @media screen and (min-width: ${getThemeBreakpointCorrect().screen_md}) {
        gap: 16px;
    }
`;
export const Aside = styled.aside`
    display: block;

    @media screen and (min-width: ${getThemeBreakpointCorrect().screen_md}) {
        flex-grow: 0;
        flex-shrink: 0;
        flex-basis: 30rem;
        padding-left: 2rem;
    }
`;

const carDetailsWrapper = css`
    max-width: 500px;
    flex: 1;
`;

const CarDetails = styled.div`
    display: flex;
    place-items: center;
    border-bottom: solid 1px #dcdde0;
`;
const detailRow = css`
    display: flex;
    flex-direction: row;
    width: 100%;
    //justify-content: space-between;
    align-content: flex-start;
    align-items: center;
`;
const pRow = css`
    width: 50%;
    font-size: 16px;
    line-height: 20px;
    display: flex;
    gap: 4px;
    color: #66676c;
`;
const pValue = css`
    font-weight: bold;
`;
const pKey = css`
    max-width: 210px;
`;

const H1 = styled.h1`
    font-size: 20px;
`;

const Section = styled.section`
    padding: 24px 0 48px 0;
    border-top: 1px solid #dcdde0;
`;

export const GalleryWrapper = styled.section`
    display: flex;
    width: 100%;
    min-height: 286px;
    position: relative;
    max-height: 500px;
    height: 100%;
    background: #ebecef;
    margin-bottom: 5rem;
    justify-content: center;
    img {
        max-height: 100%;
        max-width: 100%;
        width: auto;
        height: auto;
        display: block;
    }

    @media screen and (min-width: ${(props) => props.theme.breakpoints.screen_md}) {
        padding-right: 1rem;
    }
`;
const Price = styled.h3`
    font-weight: 700;
    margin: 0;
    line-height: 34px;
    font-size: 26px;
    color: ${carsTheme.colors.brand_primary};
    margin-top: 1rem;
    @media screen and (min-width: ${getThemeBreakpointCorrect().screen_md}) {
        margin-top: 0;
    }
`;
const DetailWrapper = styled.section<{isActive: boolean}>`
    display: flex;
    flex-direction: column;

    @media screen and (min-width: ${getThemeBreakpointCorrect().screen_md}) {
        flex-direction: row-reverse;
        display: ${(props) => (props.isActive ? "flex" : "block")};
    }
`;

const fav = (theme: Theme) => css`
    display: flex;
    border: 2px solid #66676c;
    height: 42px;
    align-items: center;
    justify-content: flex-start;
    padding: 20px;
    position: relative;

    background: #fff;
    font-weight: 600;

    font-size: 16px;
    color: #fff;
    ${bgc_primary};
    border: 2px solid ${carsTheme.colors.brand_primary};
    border-radius: 4px;
    cursor: pointer;

    &:hover {
        ${bgc_primary_hover};
        color: ${carsTheme.colors.brand_black};
        background: #fff;
        cursor: pointer;
    }
`;

const small = css`
    font-size: 16px;
    margin-left: -8px;
    font-weight: 400;
`;

const CarDetailBreadcrumbs = styled(Breadcrumbs)`
    overflow-x: auto;
    overflow-y: hidden;
    flex-wrap: wrap;
`;

export const CarsDetailForSale = withRouter(CarsDetailForSaleC);
